import { useState, useMemo, memo, useCallback } from 'react';
import { useMessagingStore } from '@/store/messaging.store';
import { useConversations } from '@/hooks/useMessages';
import { formatDistanceToNow } from 'date-fns';
import type { ConversationWithPreview } from '@/types/messaging';

import { IconButton } from '../common/Button';
import TabButton from '../common/TabButton';
import Input from '../common/Input';
import FaIcon from '../common/FaIcon';
import EmptyState from '../common/EmptyState';

type FilterType = 'all' | 'direct' | 'group' | 'incident';

export function ConversationList() {
  const {
    setActiveConversation,
    unreadCounts,
    onlineUsers,
    conversationFilter,
    setConversationFilter,
    conversationSearch,
    setConversationSearch,
    mutedConversations,
  } = useMessagingStore();
  const { data, isLoading, error } = useConversations();
  const [searchFocused, setSearchFocused] = useState(false);

  // Memoized click handler to avoid recreating functions
  const handleConversationClick = useCallback((conversationId: string) => {
    setActiveConversation(conversationId);
  }, [setActiveConversation]);

  // Filter and search conversations
  const filteredConversations = useMemo(() => {
    if (!data?.data) return [];

    let filtered = data.data;

    // Apply type filter
    if (conversationFilter !== 'all') {
      filtered = filtered.filter(c => c.type === conversationFilter);
    }

    // Apply search filter
    if (conversationSearch.trim()) {
      const search = conversationSearch.toLowerCase();
      filtered = filtered.filter(c => {
        const displayName = getConversationDisplayName(c).toLowerCase();
        return displayName.includes(search);
      });
    }

    return filtered;
  }, [data?.data, conversationFilter, conversationSearch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-theme-tertiary px-4">
        <FaIcon icon="triangle-exclamation" className="w-12 h-12 mb-3 text-red-400" />
        <p className="text-sm text-center">Failed to load conversations</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="p-2 border-b border-theme-primary">
        <div className={`relative flex items-center rounded-lg bg-surface-tertiary ${searchFocused ? 'ring-2 ring-primary-500' : ''}`}>
          <FaIcon icon="magnifying-glass" className="w-4 h-4 text-gray-400 ml-3" />
          <Input
            type="text"
            placeholder="Search conversations..."
            aria-label="Search conversations"
            value={conversationSearch}
            onChange={(e) => setConversationSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            className="bg-transparent border-none shadow-none focus:ring-2 focus:ring-primary-500/50 rounded px-2 py-1.5"
          />
          {conversationSearch && (
            <IconButton
              icon={<FaIcon icon="xmark" className="w-4 h-4" />}
              label="Clear search"
              variant="ghost"
              size="sm"
              onClick={() => setConversationSearch('')}
              className="mr-1"
            />
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex border-b border-theme-primary">
        {(['all', 'direct', 'group', 'incident'] as FilterType[]).map((filter) => (
          <TabButton key={filter} active={conversationFilter === filter} onClick={() => setConversationFilter(filter)} className="flex-1 justify-center">
            {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
          </TabButton>
        ))}
      </div>

      {/* Conversation list */}
      <div className="overflow-y-auto flex-1">
        {filteredConversations.length === 0 ? (
          <EmptyState
            icon="comments"
            title={conversationSearch ? 'No conversations found' : 'No conversations yet'}
            description={conversationSearch ? 'Try a different search term' : 'Start a new conversation to get started'}
            size="sm"
          />
        ) : (
          filteredConversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              unreadCount={unreadCounts[conversation.id] || 0}
              isOnline={
                conversation.type === 'direct' &&
                conversation.other_participant?.id
                  ? onlineUsers.has(conversation.other_participant.id)
                  : false
              }
              isMuted={mutedConversations.has(conversation.id)}
              onClick={() => handleConversationClick(conversation.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface ConversationItemProps {
  conversation: ConversationWithPreview;
  unreadCount: number;
  isOnline: boolean;
  isMuted: boolean;
  onClick: () => void;
}

const ConversationItem = memo(function ConversationItem({ conversation, unreadCount, isOnline, isMuted, onClick }: ConversationItemProps) {
  const displayName = getConversationDisplayName(conversation);
  const avatarLetter = displayName.charAt(0).toUpperCase();

  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 text-left transition-colors"
    >
      {/* Avatar with online indicator */}
      <div className="relative flex-shrink-0">
        {conversation.avatar_url || conversation.other_participant?.avatar_url ? (
          <img
            src={conversation.avatar_url || conversation.other_participant?.avatar_url}
            alt={displayName}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${getAvatarColor(conversation.type)}`}
          >
            {conversation.type === 'incident' ? (
              <FaIcon icon="triangle-exclamation" className="w-5 h-5" />
            ) : conversation.type === 'group' ? (
              <FaIcon icon="users" className="w-5 h-5" />
            ) : (
              avatarLetter
            )}
          </div>
        )}
        {/* Online indicator */}
        {conversation.type === 'direct' && (
          <div
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
              isOnline ? 'bg-green-500' : 'bg-gray-400'
            }`}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <h4 className="text-sm font-medium text-theme-primary truncate" title={displayName}>
              {displayName}
            </h4>
            {isMuted && (
              <FaIcon icon="volume-xmark" className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            )}
          </div>
          {conversation.last_message_at && (
            <span className="text-xs text-theme-tertiary flex-shrink-0 ml-2">
              {formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: false })}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-sm text-theme-tertiary truncate" title={conversation.last_message_preview || 'No messages yet'}>
            {conversation.last_message_preview || 'No messages yet'}
          </p>
          {unreadCount > 0 && (
            <span className="flex-shrink-0 ml-2 bg-primary-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        {conversation.type !== 'direct' && conversation.participant_count && (
          <p className="text-xs text-theme-tertiary mt-0.5">
            {conversation.participant_count} participants
          </p>
        )}
      </div>
    </button>
  );
});

function getConversationDisplayName(conversation: ConversationWithPreview): string {
  if (conversation.type === 'direct' && conversation.other_participant) {
    return conversation.other_participant.name;
  }
  return conversation.name || 'Unnamed conversation';
}

function getAvatarColor(type: string): string {
  switch (type) {
    case 'direct':
      return 'bg-primary-500';
    case 'group':
      return 'bg-purple-500';
    case 'incident':
      return 'bg-orange-500';
    default:
      return 'bg-gray-500';
  }
}
